// @greenlight-spec: 59-platform-agent.md#dashboard-ia
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

import { AppIconTile } from '@/components/greenlight/AppIconTile';
import { IntegrationTile } from '@/components/greenlight/IntegrationTile';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import {
  AGENTS,
  APP_TARGETS,
  INTEGRATION_TARGETS,
  type AgentDef,
  type TargetKind,
} from './fixtures';
import { AgentTile, RoadmapTag, agentById } from './shared';

const NEEDS: Record<TargetKind, string> = {
  integration: 'Pick an integration',
  app: 'Pick an app',
  brief: 'Just a brief',
  none: 'Runs now',
};

const BRIEF_HINT: Record<string, string> = {
  'integration-tester': 'Optional: the symptom, e.g. “apps get 401 after about an hour”',
  'knowledge-explorer': 'Optional: what to focus on, e.g. “how invoices relate to purchase orders”',
  'app-patcher': 'Optional: which advisory or dependency to patch',
  assistant: 'What do you want to know? e.g. “What changed in this install this week?”',
};

const selectedRing = 'bg-muted font-semibold ring-1 ring-input';

/**
 * Starts a session the way a person thinks about it: pick the job, then the one
 * thing it needs. Opened from the catalog with no agent, or from an agent's page
 * with that agent fixed.
 */
export function StartSessionDialog({
  open,
  onOpenChange,
  agentId: fixedAgentId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentId?: string;
}): React.JSX.Element {
  const navigate = useNavigate();
  const startable = AGENTS.filter((a) => a.enabled).sort(
    (a, b) => Number(a.status === 'roadmap') - Number(b.status === 'roadmap'),
  );
  const [agentId, setAgentId] = React.useState<string>(fixedAgentId ?? 'integration-tester');
  const [target, setTarget] = React.useState<string | null>(null);
  const [brief, setBrief] = React.useState('');
  const agent = agentById(agentId);

  React.useEffect(() => {
    if (!open) return;
    setAgentId(fixedAgentId ?? 'integration-tester');
    setTarget(null);
    setBrief('');
  }, [open, fixedAgentId]);

  if (!agent) return <></>;
  const needsTarget = agent.targetKind === 'integration' || agent.targetKind === 'app';
  const ready = needsTarget
    ? target !== null
    : agent.targetKind === 'brief'
      ? brief.trim().length > 0
      : true;
  const disclosure =
    agent.id === 'integration-tester' &&
    INTEGRATION_TARGETS.find((t) => t.name === target)?.secretBearing;

  const start = () => {
    onOpenChange(false);
    const where = target ? ` on ${target}` : '';
    if (agent.startSessionId) {
      navigate(
        `/agents/${agent.id}/sessions/${agent.startSessionId}${target ? `?target=${encodeURIComponent(target)}` : ''}`,
      );
      toast.success(`${agent.name} started${where}`);
    } else {
      toast.success(`${agent.name} queued${where}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('gap-0 p-0', fixedAgentId ? 'max-w-xl' : 'max-w-3xl')}>
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle>
            {fixedAgentId ? agent.startLabel.replace('…', '') : 'Start a session'}
          </DialogTitle>
          <DialogDescription className={fixedAgentId ? undefined : 'sr-only'}>
            {fixedAgentId ? agent.description : 'Agent, target and brief'}
          </DialogDescription>
        </DialogHeader>

        <div className={cn('grid min-h-0', !fixedAgentId && 'md:grid-cols-[260px_minmax(0,1fr)]')}>
          {!fixedAgentId && (
            <div
              role="radiogroup"
              aria-label="Agent"
              className="max-h-[480px] space-y-1 overflow-y-auto border-b border-border p-3 md:border-b-0 md:border-r"
            >
              {startable.map((a, i) => (
                <React.Fragment key={a.id}>
                  {a.status === 'roadmap' && startable[i - 1]?.status !== 'roadmap' && (
                    <div className="px-2.5 pb-1 pt-3 font-mono text-[10px] uppercase tracking-[0.06em]">
                      Coming later
                    </div>
                  )}
                  <AgentOption
                    agent={a}
                    selected={a.id === agentId}
                    onSelect={() => {
                      setAgentId(a.id);
                      setTarget(null);
                    }}
                  />
                </React.Fragment>
              ))}
            </div>
          )}

          <div className="min-w-0 space-y-4 p-5">
            {!fixedAgentId && (
              <div className="flex items-start gap-3">
                <AgentTile agentId={agent.id} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-[14px] font-semibold">
                    {agent.name}
                    {agent.status === 'roadmap' && <RoadmapTag />}
                  </div>
                  <p className="text-[12.5px] leading-relaxed text-muted-foreground">
                    {agent.description}
                  </p>
                </div>
              </div>
            )}

            {agent.targetKind === 'integration' && (
              <fieldset className="space-y-2">
                <legend className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.06em]">
                  Integration
                </legend>
                <div
                  role="radiogroup"
                  aria-label="Integration"
                  className="grid grid-cols-2 gap-2 sm:grid-cols-3"
                >
                  {INTEGRATION_TARGETS.map((t) => (
                    <button
                      key={t.slug}
                      type="button"
                      role="radio"
                      aria-checked={target === t.name}
                      onClick={() => setTarget(t.name)}
                      className={cn(
                        'flex min-w-0 items-center gap-2 rounded-gl-icon border border-border px-2.5 py-2 text-left text-[13px] hover:bg-accent',
                        target === t.name && selectedRing,
                      )}
                    >
                      <IntegrationTile
                        slug={t.slug}
                        catalogKey={t.slug}
                        displayName={t.name}
                        size={24}
                      />
                      <span className="truncate">{t.name}</span>
                    </button>
                  ))}
                </div>
              </fieldset>
            )}

            {agent.targetKind === 'app' && (
              <fieldset>
                <legend className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.06em]">
                  App
                </legend>
                <div role="radiogroup" aria-label="App" className="grid gap-1.5 sm:grid-cols-2">
                  {APP_TARGETS.map((t) => (
                    <button
                      key={t.slug}
                      type="button"
                      role="radio"
                      aria-checked={target === t.name}
                      onClick={() => setTarget(t.name)}
                      className={cn(
                        'flex min-w-0 items-center gap-2.5 rounded-gl-icon border border-border px-2.5 py-2 text-left hover:bg-accent',
                        target === t.name && selectedRing,
                      )}
                    >
                      <AppIconTile name={t.name} slug={t.slug} size={28} />
                      <span className="min-w-0">
                        <span className="block truncate text-[13px]">{t.name}</span>
                        <span className="block truncate font-mono text-[11px] font-normal text-muted-foreground">
                          {t.note}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </fieldset>
            )}

            <div>
              <label
                htmlFor="session-brief"
                className="mb-2 block font-mono text-[10.5px] uppercase tracking-[0.06em]"
              >
                {agent.targetKind === 'brief' ? 'Your question' : 'Brief'}
              </label>
              <Textarea
                id="session-brief"
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                rows={agent.targetKind === 'brief' ? 4 : 2}
                placeholder={BRIEF_HINT[agent.id] ?? 'Optional: anything the agent should focus on'}
                className="resize-none text-[13.5px]"
              />
            </div>

            {disclosure && (
              <p className="flex items-start gap-2 rounded-gl-icon bg-muted px-3 py-2 text-[12px] leading-relaxed">
                <ShieldAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                The direct route hands {target}’s raw credential to the agent for this test. The
                delivery is recorded in the credential’s access ledger like any other disclosure.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 border-t border-border px-6 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={start} disabled={!ready}>
            {target ? `Start on ${target}` : 'Start session'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AgentOption({
  agent,
  selected,
  onSelect,
}: {
  agent: AgentDef;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-gl-icon px-2.5 py-2 text-left hover:bg-accent',
        selected && selectedRing,
      )}
    >
      <AgentTile agentId={agent.id} size="sm" />
      <span className="min-w-0">
        <span className="block truncate text-[13px]">{agent.name}</span>
        <span className="block truncate font-mono text-[10.5px] font-normal uppercase tracking-[0.04em] text-muted-foreground">
          {NEEDS[agent.targetKind]}
        </span>
      </span>
    </button>
  );
}
